import React, { useState } from 'react';
import { Briefcase, Plane, ChevronDown, Clock } from 'lucide-react';
import { colors, borderRadius } from '../../styles/tokens';
import { useBlockEditor } from '../../hooks/useBlockEditor';
import { EditableField, BaggageEditor } from '../editing';

function getWaitTime(currentSegment, nextSegment) {
  if (!nextSegment) return null;
  const currArrival = currentSegment.arrival || currentSegment.arrival_time || '';
  const nextDeparture = nextSegment.departure || nextSegment.departure_time || '';
  if (!currArrival || !nextDeparture) return null;
  const [arrH, arrM] = currArrival.split(':').map(Number);
  const [depH, depM] = nextDeparture.split(':').map(Number);
  const wait = (depH * 60 + depM) - (arrH * 60 + arrM);
  if (wait <= 0) return null;
  const hours = Math.floor(wait / 60);
  const mins = wait % 60;
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
}

function BaggageIconsReadonly({ baggage }) {
  if (!baggage || typeof baggage !== 'object') return null;
  const hasBackpack = baggage.personal_item ?? true;
  const hasCarryOn = baggage.carry_on ?? false;
  const hasChecked = (baggage.checked_bags ?? 0) > 0;
  const active = colors.accent;
  const inactive = '#cbd5e1';
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px' }}>
      <Briefcase size={14} color={hasBackpack ? active : inactive} strokeWidth={1.5} />
      <Briefcase size={16} color={hasCarryOn ? active : inactive} strokeWidth={1.5} />
      <Briefcase size={20} color={hasChecked ? active : inactive} strokeWidth={1.5} />
    </div>
  );
}

export default function FlightCard({ block, onRemove, quoteId, onBlockUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const editable = !!quoteId;
  const { saving, updateField, updateDetail, updateDetails, updateSegment, isSaved } =
    useBlockEditor(quoteId, block, onBlockUpdate);

  const details = block.details || {};
  const segments = details.segments || [];
  const hasConnection = segments.length > 1;
  const firstSegment = segments[0] || {};
  const lastSegment = segments[segments.length - 1] || {};

  const getCompany = (seg) => seg.company || seg.airline || '';
  const getFlightNumber = (seg) => seg.flight_number || seg.flightNumber || '';
  const getDeparture = (seg) => seg.departure || seg.departure_time || '';
  const getArrival = (seg) => seg.arrival || seg.arrival_time || '';
  const getOrigin = (seg) => seg.origin || '';
  const getDestination = (seg) => seg.destination || '';

  const displayData = {
    company: getCompany(firstSegment) || details.airline,
    flightNumber: getFlightNumber(firstSegment) || details.flight_number,
    departure: getDeparture(firstSegment),
    origin: getOrigin(firstSegment),
    arrival: getArrival(lastSegment),
    destination: getDestination(lastSegment),
    baggage: details.baggage || firstSegment.baggage,
    baggageText: details.baggage_text,
  };

  const hasDifferentCarrier = segments.some((seg, i) =>
    i > 0 && (getCompany(seg) !== getCompany(firstSegment) || getFlightNumber(seg) !== getFlightNumber(firstSegment))
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-BR');
  };

  const handleSegField = (segIndex, field, value) => {
    const seg = segments[segIndex] || {};
    const realField = (field === 'departure' && seg.departure_time !== undefined) ? 'departure_time'
      : (field === 'arrival' && seg.arrival_time !== undefined) ? 'arrival_time'
      : (field === 'company' && seg.airline !== undefined) ? 'airline'
      : (field === 'flightNumber' && seg.flightNumber !== undefined) ? 'flightNumber'
      : field;
    updateSegment(segIndex, realField, value);
  };

  const handleBaggageSave = async (baggageObj, baggageText) => {
    await updateDetails({
      baggage: { ...(details.baggage || {}), ...baggageObj },
      baggage_text: baggageText,
    }, 'baggage');
  };

  // Helper: renderiza campo editável ou texto estático
  const E = (value, onSave, opts = {}) => {
    if (!editable) return opts.fallback || value || opts.placeholder || '-';
    return (
      <EditableField
        value={value}
        onSave={onSave}
        type={opts.type || 'text'}
        saving={saving}
        saved={opts.saved}
        placeholder={opts.placeholder || '-'}
        style={opts.style}
        validate={opts.validate}
      />
    );
  };

  const renderSegmentsDeparture = () => segments.map((seg, i) => {
    const showCarrier = i > 0 && hasDifferentCarrier &&
      (getCompany(seg) !== getCompany(segments[i - 1]) || getFlightNumber(seg) !== getFlightNumber(segments[i - 1]));
    return (
      <React.Fragment key={i}>
        <div>
          <p style={{ fontSize: '1rem', fontWeight: 700, color: colors.text.primary, margin: 0 }}>
            {E(getDeparture(seg), (v) => handleSegField(i, 'departure', v), { type: 'time', saved: isSaved(`segment_${i}_departure`) || isSaved(`segment_${i}_departure_time`), fallback: getDeparture(seg) || '--:--' })}
            <span style={{ fontWeight: 400, fontSize: '0.875rem' }}>
              {' - '}{E(getOrigin(seg), (v) => handleSegField(i, 'origin', v), { saved: isSaved(`segment_${i}_origin`), style: { fontWeight: 400 }, fallback: getOrigin(seg) })}
            </span>
          </p>
          {showCarrier && (
            <p style={{ fontSize: '0.75rem', color: colors.text.muted, margin: '0.125rem 0 0 0' }}>
              {E(getCompany(seg), (v) => handleSegField(i, 'company', v), { saved: isSaved(`segment_${i}_company`) || isSaved(`segment_${i}_airline`) })}
              {' • '}
              {E(getFlightNumber(seg), (v) => handleSegField(i, 'flight_number', v), { saved: isSaved(`segment_${i}_flight_number`) || isSaved(`segment_${i}_flightNumber`) })}
            </p>
          )}
        </div>
        {i < segments.length - 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: colors.text.muted, fontSize: '0.75rem', padding: '0.25rem 0' }}>
            <Clock size={12} />
            <span>Espera {getWaitTime(seg, segments[i + 1])} em {getDestination(seg)}</span>
          </div>
        )}
      </React.Fragment>
    );
  });

  const renderSegmentsArrival = () => segments.map((seg, i) => {
    const showCarrier = i > 0 && hasDifferentCarrier &&
      (getCompany(seg) !== getCompany(segments[i - 1]) || getFlightNumber(seg) !== getFlightNumber(segments[i - 1]));
    return (
      <React.Fragment key={i}>
        <div>
          <p style={{ fontSize: '1rem', fontWeight: 700, color: colors.text.primary, margin: 0 }}>
            {E(getArrival(seg), (v) => handleSegField(i, 'arrival', v), { type: 'time', saved: isSaved(`segment_${i}_arrival`) || isSaved(`segment_${i}_arrival_time`), fallback: getArrival(seg) || '--:--' })}
            <span style={{ fontWeight: 400, fontSize: '0.875rem' }}>
              {' - '}{E(getDestination(seg), (v) => handleSegField(i, 'destination', v), { saved: isSaved(`segment_${i}_destination`), style: { fontWeight: 400 }, fallback: getDestination(seg) })}
            </span>
          </p>
          {showCarrier && <div style={{ height: '1.125rem' }}></div>}
        </div>
        {i < segments.length - 1 && <div style={{ height: '1.75rem' }}></div>}
      </React.Fragment>
    );
  });

  return (
    <div style={{
      backgroundColor: colors.card, border: `1px solid ${colors.border}`,
      borderRadius: borderRadius.md, padding: '1.5rem', position: 'relative',
    }}>
      <button onClick={() => onRemove(block.id)} style={{
        position: 'absolute', top: '1rem', right: '1rem',
        background: 'none', border: 'none', cursor: 'pointer',
        color: colors.text.muted, fontSize: '1.25rem',
      }}>×</button>

      <div style={{ display: 'flex', gap: '1rem' }}>
        <div style={{
          width: '48px', height: '48px', backgroundColor: `${colors.primary}10`,
          borderRadius: borderRadius.sm, display: 'flex', alignItems: 'center',
          justifyContent: 'center', flexShrink: 0,
        }}>
          <Plane size={24} color={colors.primary} />
        </div>

        <div style={{ flex: 1 }}>
          <div
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', cursor: hasConnection ? 'pointer' : 'default' }}
            onClick={() => hasConnection && setExpanded(!expanded)}
          >
            <h4 style={{ fontSize: '1.125rem', fontWeight: 600, color: colors.primary, margin: 0 }}>
              {E(block.title, (v) => updateField('title', v), { saved: isSaved('title'), fallback: block.title })}
            </h4>
            {hasConnection && (
              <ChevronDown size={20} color={colors.text.muted}
                style={{ transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }} />
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            {/* Companhia */}
            <div>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Companhia aérea</p>
              <p style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary, margin: 0 }}>
                {E(displayData.company, (v) => segments.length > 0 ? handleSegField(0, 'company', v) : updateDetail('airline', v), {
                  saved: isSaved('segment_0_company') || isSaved('segment_0_airline') || isSaved('airline'),
                  fallback: displayData.company,
                })}
                {' • '}
                {E(displayData.flightNumber, (v) => segments.length > 0 ? handleSegField(0, 'flight_number', v) : updateDetail('flight_number', v), {
                  saved: isSaved('segment_0_flight_number') || isSaved('segment_0_flightNumber') || isSaved('flight_number'),
                  fallback: displayData.flightNumber,
                })}
              </p>
            </div>

            {/* Data */}
            <div>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Data do voo</p>
              <p style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary, margin: 0 }}>
                {E(block.date, (v) => updateField('date', v), { type: 'date', saved: isSaved('date'), fallback: formatDate(block.date) })}
              </p>
            </div>

            {!expanded ? (
              <>
                <div>
                  <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Partida</p>
                  <p style={{ fontSize: '1rem', fontWeight: 700, color: colors.text.primary, margin: 0 }}>
                    {E(displayData.departure, (v) => segments.length > 0 ? handleSegField(0, 'departure', v) : updateDetail('departure_time', v), {
                      type: 'time', saved: isSaved('segment_0_departure') || isSaved('segment_0_departure_time'),
                      fallback: displayData.departure || '--:--',
                    })}
                    <span style={{ fontWeight: 400, fontSize: '0.875rem' }}>
                      {' - '}{E(displayData.origin, (v) => segments.length > 0 ? handleSegField(0, 'origin', v) : updateDetail('origin', v), {
                        saved: isSaved('segment_0_origin'), style: { fontWeight: 400 }, fallback: displayData.origin,
                      })}
                    </span>
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Chegada</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <p style={{ fontSize: '1rem', fontWeight: 700, color: colors.text.primary, margin: 0 }}>
                      {E(displayData.arrival, (v) => { const li = segments.length - 1; segments.length > 0 ? handleSegField(li, 'arrival', v) : updateDetail('arrival_time', v); }, {
                        type: 'time', saved: isSaved(`segment_${segments.length - 1}_arrival`) || isSaved(`segment_${segments.length - 1}_arrival_time`),
                        fallback: displayData.arrival || '--:--',
                      })}
                      <span style={{ fontWeight: 400, fontSize: '0.875rem' }}>
                        {' - '}{E(displayData.destination, (v) => { const li = segments.length - 1; segments.length > 0 ? handleSegField(li, 'destination', v) : updateDetail('destination', v); }, {
                          saved: isSaved(`segment_${segments.length - 1}_destination`), style: { fontWeight: 400 }, fallback: displayData.destination,
                        })}
                      </span>
                    </p>
                    {hasConnection && (
                      <span style={{ fontSize: '0.625rem', color: colors.text.muted, backgroundColor: colors.background, padding: '0.125rem 0.375rem', borderRadius: '4px' }}>
                        {segments.length - 1} {segments.length === 2 ? 'parada' : 'paradas'}
                      </span>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div>
                  <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Partida</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {renderSegmentsDeparture()}
                  </div>
                </div>
                <div>
                  <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Chegada</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {renderSegmentsArrival()}
                  </div>
                </div>
              </>
            )}

            {/* Bagagem */}
            <div>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Bagagem</p>
              {editable ? (
                <BaggageEditor
                  baggage={displayData.baggage}
                  baggageText={displayData.baggageText}
                  onSave={handleBaggageSave}
                  saving={saving}
                  saved={isSaved('baggage')}
                />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <BaggageIconsReadonly baggage={displayData.baggage} />
                  <span style={{ fontSize: '0.875rem', fontWeight: 500, color: colors.text.primary }}>
                    {displayData.baggageText || 'Não informada'}
                  </span>
                </div>
              )}
            </div>

            {/* Preço */}
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Valor</p>
              <p style={{ fontSize: '1.25rem', fontWeight: 700, color: colors.accent, margin: 0 }}>
                {E(block.price || 0, (v) => updateField('price', v), {
                  type: 'currency', saved: isSaved('price'),
                  validate: (v) => v < 0 ? 'Preço inválido' : null,
                  fallback: `R$ ${(block.price || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
